// /pages/FleetManagement/Trucks/EditTruckPage.tsx

// import React, { useEffect, useState } from 'react';
// import {
//     Box,
//     Card,
//     CardHeader,
//     CardBody,
//     Heading,
//     Text,
//     Spinner,
//     Center,
//     Alert,
//     AlertIcon,
//     VStack,
// } from '@chakra-ui/react';
// import { useNavigate, useParams } from 'react-router-dom';
// import { AdminLayout } from '@/components/layout/AdminLayout';
// import { TruckForm } from '@/components/forms/TruckForm';
// import { useThemeColors } from '@/hooks/useThemeColors';
// import { apiClient } from '@/lib/api/client';
// import { useToastNotification } from '@/lib/utils/useToastNotification';

// // Define the expected structure of truck data from the API
// interface TruckData {
//     id: number;
//     plate_number: string;
//     condition: string;
//     is_active: number;
// }

// export default function EditTruckPage() {
//     const colors = useThemeColors();
//     const navigate = useNavigate();
//     const { showError } = useToastNotification();

//     // 1. Parameter extraction and conversion (Patterned after EditUserPage)
//     const { id } = useParams<{ id: string }>();
//     const truckId = id ? parseInt(id, 10) : undefined;

//     // 2. Data fetching state
//     const [truck, setTruck] = useState<TruckData | null>(null);
//     const [isLoading, setIsLoading] = useState(true);
//     const [error, setError] = useState<string | null>(null);

//     // --- Data Fetching Effect ---
//     useEffect(() => {
//         // CRITICAL VALIDATION CHECK
//         if (!truckId || isNaN(truckId)) {
//             setError('Invalid truck ID provided. Check the URL parameter.');
//             setIsLoading(false);
//             return;
//         }

//         const fetchTruck = async () => {
//             setIsLoading(true); // Ensure loading is true on initial fetch
//             try {
//                 // Use the dedicated GET endpoint
//                 const response = await apiClient.get(`/trucks/get-truck-by-id/${truckId}`);
//                 // Assuming data structure: response.data.data or response.data
//                 const fetchedData = response.data.data || response.data; 
//                 setTruck(fetchedData);
//                 setError(null); // Clear any previous error
//             } catch (err: any) {
//                 setError('Failed to load truck details. Please try again.');
//                 showError(err);
//             } finally {
//                 setIsLoading(false);
//             }
//         };

//         fetchTruck();
//     }, [truckId, showError]);


//     // --- Navigation Handlers (Patterned after EditUserPage) ---
//     const handleSuccess = () => {
//         navigate('/fleet-management/trucks');
//     };

//     const handleCancel = () => {
//         navigate('/fleet-management/trucks');
//     };

//     // --- Conditional Rendering ---
    
//     // 3. Loading State
//     if (isLoading) {
//         return (
//             <AdminLayout>
//                 <Center h="100vh">
//                     <VStack>
//                         <Spinner size="xl" color="blue.500" />
//                         <Text color={colors.textSecondary}>Loading truck data...</Text>
//                     </VStack>
//                 </Center>
//             </AdminLayout>
//         );
//     }
    
//     // 4. Error State (Including Invalid ID)
//     if (error || !truck) {
//         return (
//             <AdminLayout>
//                 <Box p={4}>
//                     <Alert status="error">
//                         <AlertIcon />
//                         {error || 'Truck data could not be retrieved.'}
//                     </Alert>
//                 </Box>
//             </AdminLayout>
//         );
//     }

//     // 5. Success/Ready State
//     return (
//         <AdminLayout>
//             <Box p={4} h="100%" overflow="auto">
//                 <Card>
//                     <CardHeader py={3}> 
//                         <Heading size="md" mb={1}>
//                             Edit Truck: {truck.plate_number}
//                         </Heading>
//                         <Text fontSize="sm" color={colors.text}>
//                             Update the details and status of the truck.
//                         </Text>
//                     </CardHeader>
                    
//                     <CardBody>
//                         {/* Pass the ID and the fetched data to the form */}
//                         <TruckForm 
//                             truckId={truckId} 
//                             initialData={truck} // The fetched data
//                             onSuccess={handleSuccess} 
//                             onCancel={handleCancel} 
//                         />
//                     </CardBody>
//                 </Card>
//             </Box>
//         </AdminLayout>
//     );
// }

import React, { useEffect, useState } from 'react';
import {
    Box,
    Card,
    CardHeader,
    CardBody,
    Heading,
    Text,
    Spinner,
    Center,
    Alert,
    AlertIcon,
    VStack,
} from '@chakra-ui/react';
import { useNavigate, useParams } from 'react-router-dom';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { TruckForm } from '@/components/forms/TruckForm';
import { useThemeColors } from '@/hooks/useThemeColors';
import { apiClient } from '@/lib/api/client';
import { useToastNotification } from '@/lib/utils/useToastNotification';

interface TruckData {
    id: number;
    plate_number: string;
    condition: string;
    is_active: number;
}

export default function EditTruckPage() {
    const colors = useThemeColors();
    const navigate = useNavigate();
    const { showError } = useToastNotification();

    const { id } = useParams<{ id: string }>();
    const truckId = id ? parseInt(id, 10) : undefined;

    const [truck, setTruck] = useState<TruckData | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!truckId || isNaN(truckId)) {
            setError('Invalid truck ID provided.');
            setIsLoading(false);
            return;
        }

        const fetchTruck = async () => {
            setIsLoading(true);
            try {
                const response = await apiClient.get(`/trucks/get-truck-by-id/${truckId}`);
                const fetchedData = response.data.data || response.data; 
                setTruck(fetchedData);
                setError(null);
            } catch (err: any) {
                setError('Failed to load truck details.');
                showError(err);
            } finally {
                setIsLoading(false);
            }
        };

        fetchTruck();
    }, [truckId, showError]);

    const handleSuccess = () => navigate('/fleet-management/trucks');
    const handleCancel = () => navigate('/fleet-management/trucks');

    // Loading State
    if (isLoading) {
        return (
            <AdminLayout>
                <Center h="full">
                    <VStack>
                        <Spinner size="xl" color="blue.500" />
                        <Text color={colors.textSecondary}>Loading truck data...</Text>
                    </VStack>
                </Center>
            </AdminLayout>
        );
    }

    // Patterned after CreateTruckPage with integrated Scroll Fix
    return (
        <AdminLayout>
            {/* 1. Use h="full", overflow="hidden", and flex column for the outer container */}
            <Box p={4} h="full" overflow="hidden" display="flex" flexDirection="column">
                
                {/* 2. Make Card take full height and act as a flex container */}
                <Card h="full" display="flex" flexDirection="column">
                    
                    {/* 3. Header stays fixed at the top (flexShrink 0) */}
                    <CardHeader flexShrink={0} py={3}> 
                        <Heading size="md" mb={1}>
                            {error ? 'Error' : `Edit Truck: ${truck?.plate_number}`}
                        </Heading>
                        <Text fontSize="sm" color={colors.text}>
                            Update the details and status of the truck.
                        </Text>
                    </CardHeader>
                    
                    {/* 4. This is the only part that scrolls (overflowY="auto" and flex="1") */}
                    <CardBody overflowY="auto" flex="1" minH={0}>
                        {error || !truck ? (
                            <Alert status="error">
                                <AlertIcon />
                                {error || 'Truck data could not be retrieved.'}
                            </Alert>
                        ) : (
                            <TruckForm 
                                key={truck.id} // Forces fresh render when data arrives
                                truckId={truckId} 
                                initialData={truck} 
                                onSuccess={handleSuccess} 
                                onCancel={handleCancel} 
                            />
                        )}
                    </CardBody>
                </Card>
            </Box>
        </AdminLayout>
    );
}
